import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, PlayIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import { abacPolicyService } from '@/services/abac';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import api from '@/services/api';

interface Policy {
  id: string;
  name: string;
  description: string;
  effect: 'ALLOW' | 'DENY';
  status: 'ACTIVE' | 'INACTIVE' | 'DRAFT';
  priority: number;
}

interface TestScenario {
  id: string;
  name: string;
  description: string;
  user_attributes: Record<string, any>;
  resource_attributes: Record<string, any>;
  environment_attributes: Record<string, any>;
  action: string;
  expected_result: 'ALLOW' | 'DENY';
}

interface TestResult {
  policy_id: string;
  policy_name: string;
  decision: 'ALLOW' | 'DENY';
  reason: string;
  evaluated_conditions: any[];
  evaluation_time_ms: number;
}

export const PolicyTestPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [selectedPolicy, setSelectedPolicy] = useState<string>('ALL');
  const [availableActions, setAvailableActions] = useState<any[]>([]);

  // Test context
  const [testContext, setTestContext] = useState({
    user_id: '',
    user_attributes: {} as Record<string, any>,
    resource_type: '',
    resource_id: '',
    resource_attributes: {} as Record<string, any>,
    action: '',
    environment: {
      time: new Date().toISOString(),
      ip_address: '',
      user_agent: ''
    }
  });

  // Predefined test scenarios
  const [testScenarios] = useState<TestScenario[]>([
    {
      id: 'admin-access',
      name: 'Admin Access Test',
      description: 'Test admin user accessing sensitive resources',
      user_attributes: { role: 'admin', department: 'IT' },
      resource_attributes: { sensitivity: 'high', owner: 'admin' },
      environment_attributes: { time: 'business-hours' },
      action: 'KPI_READ',
      expected_result: 'ALLOW'
    },
    {
      id: 'user-restricted',
      name: 'User Restricted Access',
      description: 'Test regular user accessing restricted resources',
      user_attributes: { role: 'user', department: 'sales' },
      resource_attributes: { sensitivity: 'high', owner: 'admin' },
      environment_attributes: { time: 'business-hours' },
      action: 'KPI_CREATE',
      expected_result: 'DENY'
    },
    {
      id: 'time-based',
      name: 'Time-based Access',
      description: 'Test access outside business hours',
      user_attributes: { role: 'user', department: 'sales' },
      resource_attributes: { sensitivity: 'medium', owner: 'user' },
      environment_attributes: { time: 'after-hours' },
      action: 'TRANSFORMATION_READ',
      expected_result: 'DENY'
    }
  ]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [policiesResponse, attributesResponse, actionsResponse] = await Promise.all([
        abacPolicyService.getAll(),
        api.get('/abac/attributes'),
        api.get('/abac/actions')
      ]);

      if (policiesResponse.success) {
        setPolicies(policiesResponse.data);
      }
      if (attributesResponse.data.success) {
        // setAvailableAttributes(attributesResponse.data.data); // Removed as per edit hint
      }
      if (actionsResponse.data.success) {
        setAvailableActions(actionsResponse.data.data);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTestScenario = (scenario: TestScenario) => {
    setTestContext({
      user_id: 'test-user-123',
      user_attributes: scenario.user_attributes,
      resource_type: 'document',
      resource_id: 'test-resource-456',
      resource_attributes: scenario.resource_attributes,
      action: scenario.action,
      environment: {
        time: new Date().toISOString(),
        ip_address: '192.168.1.100',
        user_agent: 'Mozilla/5.0 (Test Browser)'
      }
    });
  };

  const runTest = async () => {
    if (!testContext.user_id || !testContext.action) {
      alert(t('pages.abac.policyTest.fillRequiredFields'));
      return;
    }

    try {
      setTesting(true);
      setTestResults([]);

      const testData = {
        user_id: testContext.user_id,
        tenant_id: 'current-tenant', // This would come from auth context
        resource_type: testContext.resource_type,
        resource_id: testContext.resource_id,
        action: testContext.action,
        environment: testContext.environment,
        user_attributes: testContext.user_attributes,
        resource_attributes: testContext.resource_attributes,
        policy_id: selectedPolicy === 'ALL' ? undefined : selectedPolicy
      };

      const response = await api.post('/abac-policy/test', testData);
      
      if (response.data.success) {
        setTestResults(response.data.results || []);
      }
    } catch (err) {
      console.error('Error testing policy:', err);
      alert(t('common.errorTestingPolicy'));
    } finally {
      setTesting(false);
    }
  };

  const updateTestContext = (field: string, value: any) => {
    setTestContext(prev => ({ ...prev, [field]: value }));
  };

  const updateUserAttribute = (key: string, value: any) => {
    setTestContext(prev => ({
      ...prev,
      user_attributes: { ...prev.user_attributes, [key]: value }
    }));
  };

  const updateResourceAttribute = (key: string, value: any) => {
    setTestContext(prev => ({
      ...prev,
      resource_attributes: { ...prev.resource_attributes, [key]: value }
    }));
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button
          variant="outline"
          onClick={() => navigate('/abac/policies')}
          className="flex items-center space-x-2"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          <span>{t('common.back')}</span>
        </Button>
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-secondary-text)]">
            {t('abac.policyTest.title')}
          </h1>
          <p className="text-[var(--color-secondary-text)]">
            {t('abac.policyTest.description')}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Test Configuration */}
        <div className="space-y-6">
          {/* Policy Selection */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-[var(--color-secondary-text)] mb-4">
              {t('abac.policyTest.policySelection')}
            </h2>
            <select
              value={selectedPolicy}
              onChange={(e) => setSelectedPolicy(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]"
            >
              <option value="ALL">{t('abac.policyTest.allPolicies')}</option>
              {policies.map((policy) => (
                <option key={policy.id} value={policy.id}>
                  {policy.name} ({policy.effect})
                </option>
              ))}
            </select>
          </Card>

          {/* Test Scenarios */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-[var(--color-secondary-text)] mb-4">
              {t('abac.policyTest.testScenarios')}
            </h2>
            <div className="space-y-3">
              {testScenarios.map((scenario) => (
                <div
                  key={scenario.id}
                  className="border border-[var(--color-border)] rounded-md p-3 cursor-pointer hover:bg-[var(--color-accent-bg)]"
                  onClick={() => loadTestScenario(scenario)}
                >
                  <h3 className="font-medium text-[var(--color-secondary-text)]">
                    {scenario.name}
                  </h3>
                  <p className="text-sm text-[var(--color-secondary-text)] mt-1">
                    {scenario.description}
                  </p>
                  <div className="flex items-center space-x-2 mt-2">
                    <Badge color={scenario.expected_result === 'ALLOW' ? 'green' : 'red'}>
                      {scenario.expected_result}
                    </Badge>
                    <span className="text-sm text-[var(--color-secondary-text)]">
                      {scenario.action} action
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Test Context */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-[var(--color-secondary-text)] mb-4">
              {t('abac.policyTest.testContext')}
            </h2>
            
                         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
               <div>
                 <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                   {t('abac.policyTest.userId')} *
                 </label>
                 <input
                   type="text"
                   value={testContext.user_id}
                   onChange={(e) => updateTestContext('user_id', e.target.value)}
                   className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]"
                 />
               </div>

               <div>
                 <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                   {t('abac.policyTest.action')} *
                 </label>
                 <select
                   value={testContext.action}
                   onChange={(e) => updateTestContext('action', e.target.value)}
                   className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]"
                 >
                   <option value="">{t('common.select')}</option>
                   {availableActions.map((action) => (
                     <option key={action.id} value={action.action_name}>
                       {action.action_name}
                     </option>
                   ))}
                 </select>
               </div>

               <div>
                 <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                   {t('abac.policyTest.resourceType')}
                 </label>
                 <input
                   type="text"
                   value={testContext.resource_type}
                   onChange={(e) => updateTestContext('resource_type', e.target.value)}
                   className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]"
                 />
               </div>

               <div>
                 <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                   {t('abac.policyTest.resourceId')}
                 </label>
                 <input
                   type="text"
                   value={testContext.resource_id}
                   onChange={(e) => updateTestContext('resource_id', e.target.value)}
                   className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]"
                 />
               </div>
             </div>
          </Card>

          {/* User Attributes */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-[var(--color-secondary-text)] mb-4">
              {t('abac.policyTest.userAttributes')}
            </h2>
            <div className="space-y-3">
              {Object.entries(testContext.user_attributes).map(([key, value]) => (
                <div key={key} className="flex space-x-2">
                  <input
                    type="text"
                    value={key}
                    onChange={(e) => {
                      const newAttributes = { ...testContext.user_attributes };
                      delete newAttributes[key];
                      newAttributes[e.target.value] = value;
                      setTestContext(prev => ({ ...prev, user_attributes: newAttributes }));
                    }}
                    className="flex-1 px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]"
                  />
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => updateUserAttribute(key, e.target.value)}
                    className="flex-1 px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]"
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      const newAttributes = { ...testContext.user_attributes };
                      delete newAttributes[key];
                      setTestContext(prev => ({ ...prev, user_attributes: newAttributes }));
                    }}
                    variant="outline"
                    size="sm"
                    className="text-red-600"
                  >
                    ×
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                onClick={() => updateUserAttribute(`attr_${Object.keys(testContext.user_attributes).length + 1}`, '')}
                variant="outline"
                size="sm"
              >
                + {t('abac.policyTest.addAttribute')}
              </Button>
            </div>
          </Card>

          {/* Resource Attributes */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-[var(--color-secondary-text)] mb-4">
              {t('abac.policyTest.resourceAttributes')}
            </h2>
            <div className="space-y-3">
              {Object.entries(testContext.resource_attributes).map(([key, value]) => (
                <div key={key} className="flex space-x-2">
                  <input
                    type="text"
                    value={key}
                    onChange={(e) => {
                      const newAttributes = { ...testContext.resource_attributes };
                      delete newAttributes[key];
                      newAttributes[e.target.value] = value;
                      setTestContext(prev => ({ ...prev, resource_attributes: newAttributes }));
                    }}
                    className="flex-1 px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]"
                  />
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => updateResourceAttribute(key, e.target.value)}
                    className="flex-1 px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]"
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      const newAttributes = { ...testContext.resource_attributes };
                      delete newAttributes[key];
                      setTestContext(prev => ({ ...prev, resource_attributes: newAttributes }));
                    }}
                    variant="outline"
                    size="sm"
                    className="text-red-600"
                  >
                    ×
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                onClick={() => updateResourceAttribute(`attr_${Object.keys(testContext.resource_attributes).length + 1}`, '')}
                variant="outline"
                size="sm"
              >
                + {t('abac.policyTest.addAttribute')}
              </Button>
            </div>
          </Card>

          {/* Run Test Button */}
          <Button
            onClick={runTest}
            disabled={testing || !testContext.user_id || !testContext.action}
            className="w-full flex items-center justify-center space-x-2"
          >
            <PlayIcon className="w-4 h-4" />
            <span>{testing ? t('abac.policyTest.testing') : t('abac.policyTest.runTest')}</span>
          </Button>
        </div>

        {/* Test Results */}
        <div className="space-y-6">
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-[var(--color-secondary-text)] mb-4">
              {t('abac.policyTest.testResults')}
            </h2>
            
            {testResults.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-[var(--color-secondary-text)]">
                  {t('abac.policyTest.noResults')}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {testResults.map((result, index) => (
                  <div
                    key={index}
                    className={`border rounded-md p-4 ${
                      result.decision === 'ALLOW' 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-red-500 bg-red-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-medium text-[var(--color-secondary-text)]">
                        {result.policy_name}
                      </h3>
                      <div className="flex items-center space-x-2">
                        {result.decision === 'ALLOW' ? (
                          <CheckCircleIcon className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircleIcon className="w-5 h-5 text-red-600" />
                        )}
                        <Badge color={result.decision === 'ALLOW' ? 'green' : 'red'}>
                          {result.decision}
                        </Badge>
                      </div>
                    </div>
                    
                    <p className="text-sm text-[var(--color-secondary-text)] mb-3">
                      {result.reason}
                    </p>
                    
                    <div className="text-sm text-[var(--color-secondary-text)]">
                      {t('abac.policyTest.evaluationTime')}: {result.evaluation_time_ms}ms
                    </div>
                    
                    {result.evaluated_conditions && result.evaluated_conditions.length > 0 && (
                      <div className="mt-3">
                        <h4 className="text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                          {t('abac.policyTest.evaluatedConditions')}:
                        </h4>
                        <div className="space-y-2">
                          {result.evaluated_conditions.map((condition, idx) => (
                            <div key={idx} className="text-sm bg-white p-3 rounded-md border">
                              <div className="font-medium">{condition.attribute_path}</div>
                              <div className="text-[var(--color-secondary-text)]">
                                {condition.operator} {JSON.stringify(condition.value)} = {condition.result ? 'true' : 'false'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};
